import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import Modal from '../../../shared/components/ui/Modal';
import PreleadLogModal, { PreleadNoteButton } from '../ui/PreleadLogModal';
import PreleadUpdateStatusModal from '../ui/PreleadUpdateStatusModal';
import WhatsAppFlowSendStatusChip from '../prelead/WhatsAppFlowSendStatusChip';
import PreleadSlaIndicator, { SLA_LABEL, getSlaColorKey } from '../prelead/PreleadSlaIndicator';
import {
  addLog,
  addPreLeadNote,
  createLead,
  getPreLeadGoogleAdsFields,
  getPreLeadMetaFields,
  getPreLeadWebsiteFormFields,
  getPreLeads,
  markPreLeadAsUnqualified
} from '../../services/api/implementation/impl';
import { capitalizeWords, formatStatusLabel } from '../../utils/stringUtils';
import {
  formatPreleadStatusNote,
  PRELEAD_STATUS_CATEGORY
} from '../../utils/preleadStatusOptions';

const RESPONSE_BUTTON_CLASS =
  'inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors';

function resolvePreleadProjectId(row) {
  const candidates = [
    row?.project_id,
    row?.whatsapp_flow_project_id,
    row?.pre_lead_list?.project_id,
    row?.interested_campaign?.project_id,
    row?.interested_campaign?.project?.id,
    row?.interested_campaign?.WhatsAppCampaign?.project_id
  ];

  const interestedCampaignSend = (row?.whatsapp_campaign_sends ?? [])
    .filter((send) => String(send?.state ?? '').trim().toLowerCase() === 'interested')
    .sort((a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0))[0];
  candidates.push(interestedCampaignSend?.WhatsAppCampaign?.project_id);

  for (const value of candidates) {
    const projectId = Number(value);
    if (Number.isFinite(projectId) && projectId > 0) return projectId;
  }
  return null;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="min-w-0 break-words font-medium text-gray-900">{value || '—'}</span>
    </div>
  );
}

function getAnswerLabel(answer) {
  if (!answer) return '—';
  return (
    answer?.questionOption?.label ||
    answer?.question_option?.label ||
    answer?.raw_text ||
    answer?.raw_payload ||
    '—'
  );
}

function getQuestionOptionLabels(answer) {
  const optionBuckets = [
    answer?.question?.options,
    answer?.question?.questionOptions,
    answer?.question?.question_options,
    answer?.question_options,
    answer?.options
  ];
  const flattenedOptions = optionBuckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
  const labels = flattenedOptions
    .map((option) =>
      String(
        option?.label ?? option?.option_label ?? option?.text ?? option?.title ?? option?.value ?? ''
      ).trim()
    )
    .filter(Boolean);
  return Array.from(new Set(labels));
}

function formatMetaFieldQuestion(question) {
  if (!question) return '—';
  return String(question)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getPreleadSource(prelead) {
  return prelead?.type || prelead?.source || prelead?.pre_lead_list?.source || '';
}

function PreleadFormSummaryCard({
  prelead,
  formMeta,
  formMetaLabel = 'Form',
  showWhatsAppAutoSend = false
}) {
  const email = prelead?.email?.trim() || '—';
  const status = prelead?.state ? formatStatusLabel(String(prelead.state)) : '—';
  const formName = formMeta?.name || null;
  const projectName = formMeta?.project_name?.trim() || null;

  if (!prelead && !formMeta) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {formMetaLabel}
        </span>
        {formName && <span className="text-sm font-medium text-gray-900">{formName}</span>}
        {projectName && <span className="text-xs text-gray-500">· {projectName}</span>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-gray-700">
          <span className="text-gray-400">Email </span>
          <span className="font-medium text-gray-900 break-all">{email}</span>
        </span>
        <span className="text-gray-700">
          <span className="text-gray-400">Status </span>
          <span className="font-medium text-gray-900">{status}</span>
        </span>
        {showWhatsAppAutoSend && (
          <span className="inline-flex items-center gap-1.5 text-gray-700">
            <span className="text-gray-400">WA Auto-send</span>
            <WhatsAppFlowSendStatusChip row={prelead} />
          </span>
        )}
      </div>
    </div>
  );
}

function FacebookMetaFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadMetaFields', preLeadId],
    queryFn: () => getPreLeadMetaFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const form = data?.form;

  return (
    <Modal width={50} name="Facebook Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || form || isLoading) && (
          <PreleadFormSummaryCard
            prelead={prelead}
            formMeta={form}
            formMetaLabel="Form"
            showWhatsAppAutoSend
          />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Answer
                    </p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function WebsiteFormFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadWebsiteFormFields', preLeadId],
    queryFn: () => getPreLeadWebsiteFormFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });
  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Website / Framer Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard prelead={prelead} formMeta={source} formMetaLabel="Form" />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          fields.length > 0 ? (
            fields.map((field, index) => (
              <div
                key={field.id ?? `${field.question}-${index}`}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {formatMetaFieldQuestion(field.question)}
                </p>
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                    Answer
                  </p>
                  <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                    {field.answer || '—'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No form fields available.</p>
          )
        )}
      </div>
    </Modal>
  );
}

function GoogleAdsFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadGoogleAdsFields', preLeadId],
    queryFn: () => getPreLeadGoogleAdsFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Google Ads Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard prelead={prelead} formMeta={source} formMetaLabel="Source" />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Answer
                    </p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function WhatsAppFlowAnswersModal({ selectedPrelead, onClose }) {
  const answers = selectedPrelead?.whatsapp_flow_question_answers ?? [];

  return (
    <Modal width={50} name="WhatsApp Flow Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {answers.length > 0 ? (
          answers
            .slice()
            .sort((a, b) => (a?.question?.question_order ?? 999) - (b?.question?.question_order ?? 999))
            .map((answer, index) => {
              const selectedAnswer = getAnswerLabel(answer);
              const selectedAnswerNorm = selectedAnswer.toLowerCase().trim();
              const otherOptions = getQuestionOptionLabels(answer).filter(
                (label) => label.toLowerCase().trim() !== selectedAnswerNorm
              );

              return (
                <div
                  key={answer.id ?? `${answer.question_id}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Question {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {answer?.question?.prompt_text || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Selected Answer
                    </p>
                    <p className="mt-1 text-sm font-medium text-emerald-900">{selectedAnswer}</p>
                  </div>
                  <div className="mt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-customBlue">
                      Other Options
                    </p>
                    {otherOptions.length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {otherOptions.map((optionLabel) => (
                          <span
                            key={optionLabel}
                            className="inline-flex rounded-full border border-customBlue/20 bg-customBlue/5 px-2.5 py-1 text-xs text-customBlue">
                            {optionLabel}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">No other options captured.</p>
                    )}
                  </div>
                </div>
              );
            })
        ) : (
          <p className="text-sm text-gray-500">No WhatsApp flow answers available.</p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Modal showing prelead details for an Inbox conversation that has pre_lead_id.
 */
const InboxPreleadDetailModal = ({ preLeadId, contactName, phoneNumber, onClose }) => {
  const queryClient = useQueryClient();
  const [notesPrelead, setNotesPrelead] = useState(null);
  const [showFacebookForm, setShowFacebookForm] = useState(false);
  const [showGoogleAdsForm, setShowGoogleAdsForm] = useState(false);
  const [showWebsiteForm, setShowWebsiteForm] = useState(false);
  const [showWhatsAppAnswers, setShowWhatsAppAnswers] = useState(false);
  const [statusUpdateRow, setStatusUpdateRow] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const numericId = Number(preLeadId);
  const enabled = Number.isFinite(numericId) && numericId > 0;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['inboxPreleadDetail', numericId],
    queryFn: () => getPreLeads({ id: numericId, limit: 1 }),
    enabled
  });

  const createPreleadLeadMutation = useMutation({
    mutationFn: (payload) => createLead(payload)
  });
  const unqualifyPreleadMutation = useMutation({
    mutationFn: ({ preLeadId: id, reason }) => markPreLeadAsUnqualified(id, reason)
  });

  const prelead =
    (data?.preLeads || []).find((p) => Number(p.id) === numericId) || data?.preLeads?.[0] || null;

  const phoneDisplay =
    [prelead?.countryCode, prelead?.phone].filter(Boolean).join(' ').trim() ||
    (phoneNumber ? `+${String(phoneNumber).replace(/^\+/, '')}` : '—');

  const list = prelead?.pre_lead_list;
  const source = getPreleadSource(prelead);
  const sourceKey = String(source).trim().toLowerCase();
  const hasWhatsAppAnswers =
    Array.isArray(prelead?.whatsapp_flow_question_answers) &&
    prelead.whatsapp_flow_question_answers.length > 0;

  const rowForNotes = prelead
    ? {
        ...prelead,
        lead_id: prelead.lead?.id ?? prelead.lead_id ?? null
      }
    : { id: numericId, name: contactName || `Prelead #${numericId}` };

  const qualifyRow = prelead
    ? {
        ...prelead,
        pre_lead_id: prelead.id,
        lead_id: prelead.lead?.id ?? prelead.lead_id ?? null,
        plotArea: prelead.plotArea ?? prelead.plot_area ?? []
      }
    : null;

  const createLeadFromPrelead = async (row) => {
    if (!row) return null;
    if (row.lead_id) return row.lead_id;

    const projectId = resolvePreleadProjectId(row);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      throw new Error('No project found for this prelead.');
    }

    const mapToIdArray = (items) =>
      Array.isArray(items) ? items.map((item) => item?.id ?? item).filter(Boolean) : [];

    const listSource = String(getPreleadSource(row)).trim().toLowerCase();
    const payload = {
      id: row.id,
      pre_lead_id: row.pre_lead_id ?? row.id,
      name: row.name || `Prelead ${row.id}`,
      countryCode: row.countryCode,
      phone_number: row.phone,
      source:
        listSource === 'facebook'
          ? 'facebook'
          : listSource === 'website'
            ? 'website'
            : row.source || listSource || 'whatsapp_outbound',
      interested_projects: [projectId]
    };

    if (row.email && row.email !== '—') payload.email = row.email;
    if (row.alternate_phone_number && row.alternate_phone_number !== '—') {
      payload.alternate_phone_number = row.alternate_phone_number;
    }
    if (row.occupation) payload.occupation = row.occupation;
    if (row.reference_name) payload.reference_name = row.reference_name;
    if (row.address) payload.address = row.address;
    if (row.gender) payload.gender = row.gender;
    if (row.type) payload.type = row.type;
    if (row.expected_closure_date) payload.expected_clouser_date = row.expected_closure_date;

    const interestedAccommodationType = mapToIdArray(row.accommodations);
    if (interestedAccommodationType.length) {
      payload.interestedAccomodationType = interestedAccommodationType;
    }

    const projectAccommodationIds = mapToIdArray(row.project_accommodations);
    if (projectAccommodationIds.length) {
      payload.accomodations = projectAccommodationIds;
    }

    const plotAreaIds = mapToIdArray(row.plotArea);
    if (plotAreaIds.length) {
      payload.plotArea = plotAreaIds;
    }

    if (row.min_estimated_budget != null && row.max_estimated_budget != null) {
      payload.expectedBudgetRange = `${row.min_estimated_budget}-${row.max_estimated_budget}`;
    } else if (row.expected_budget != null && Number(row.expected_budget) > 0) {
      payload.expectedBudgetRange = String(row.expected_budget);
    }

    const result = await createPreleadLeadMutation.mutateAsync(payload);
    return result?.id ?? result?.data?.id ?? null;
  };

  const handleUpdateStatus = async ({ categoryKey, statusLabel, notes }) => {
    const row = statusUpdateRow;
    if (!row?.id) return;

    const noteText = formatPreleadStatusNote(categoryKey, statusLabel, notes);
    setIsUpdatingStatus(true);
    try {
      if (categoryKey === PRELEAD_STATUS_CATEGORY.CONNECTED_QUALIFIED) {
        const leadId = await createLeadFromPrelead(row);
        await addPreLeadNote(row.id, noteText);
        if (leadId) {
          const userId = Number(Cookies.get('userId'));
          await addLog({
            name: 'Prelead qualify status',
            description: noteText,
            sales_user_id: Number.isFinite(userId) ? userId : undefined,
            lead_id: Number(leadId),
            file: ''
          });
        }
        toast.success('Marked as Qualified.');
        await refetch();
        queryClient.invalidateQueries({ queryKey: ['inboxConversations'] });
        setStatusUpdateRow(null);
        if (leadId) {
          onClose?.();
        }
        return;
      }

      if (categoryKey === PRELEAD_STATUS_CATEGORY.CONNECTED_UNQUALIFIED) {
        await unqualifyPreleadMutation.mutateAsync({ preLeadId: row.id, reason: statusLabel });
        await addPreLeadNote(row.id, noteText);
        toast.success('Marked as Unqualified.');
        await refetch();
        queryClient.invalidateQueries({ queryKey: ['inboxConversations'] });
        setStatusUpdateRow(null);
        return;
      }

      await addPreLeadNote(row.id, noteText);
      toast.success('Status updated.');
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['inboxConversations'] });
      setStatusUpdateRow(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not update status.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const renderQualifyActions = () => {
    if (!qualifyRow) return null;

    const currentState = String(qualifyRow.state ?? '').toLowerCase();
    const isInterested = currentState === 'interested';
    const leadId = qualifyRow.lead_id;
    const isDisqualified = qualifyRow.is_disqualified === true;

    if (leadId != null) {
      return <div className="text-sm font-semibold text-green-700">Qualified</div>;
    }
    if (isDisqualified) {
      return <div className="text-sm font-semibold text-red-700">Unqualified</div>;
    }
    if (!isInterested) return null;

    return (
      <button
        type="button"
        onClick={() => setStatusUpdateRow(qualifyRow)}
        className="inline-flex items-center rounded-lg border border-customBlue/50 bg-[#E9F5F6] px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/15 transition-colors">
        Update Status
      </button>
    );
  };

  const renderResponseButton = () => {
    if (!prelead) return null;

    if (sourceKey === 'facebook') {
      return (
        <button
          type="button"
          onClick={() => setShowFacebookForm(true)}
          className={RESPONSE_BUTTON_CLASS}>
          View Form
        </button>
      );
    }

    if (sourceKey === 'google_ads') {
      return (
        <button
          type="button"
          onClick={() => setShowGoogleAdsForm(true)}
          className={RESPONSE_BUTTON_CLASS}>
          View Form
        </button>
      );
    }

    if (sourceKey === 'website') {
      return (
        <button
          type="button"
          onClick={() => setShowWebsiteForm(true)}
          className={RESPONSE_BUTTON_CLASS}>
          View Form
        </button>
      );
    }

    if (hasWhatsAppAnswers) {
      return (
        <button
          type="button"
          onClick={() => setShowWhatsAppAnswers(true)}
          className={RESPONSE_BUTTON_CLASS}>
          View Q&A
        </button>
      );
    }

    return null;
  };

  return (
    <>
      <Modal width={48} name="Prelead details" onClose={onClose}>
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading prelead…
            </div>
          ) : isError ? (
            <div className="space-y-3 py-4 text-center">
              <p className="text-sm text-red-700">
                {error?.response?.data?.message || error?.message || 'Could not load prelead.'}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-primary/90">
                Try again
              </button>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 space-y-2.5">
                <DetailRow
                  label="Name"
                  value={
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <PreleadSlaIndicator slaColor={prelead?.sla_color} />
                      <span>
                        {prelead?.name
                          ? capitalizeWords(String(prelead.name))
                          : contactName
                            ? capitalizeWords(String(contactName))
                            : `Prelead #${numericId}`}
                      </span>
                    </span>
                  }
                />
                <DetailRow label="Phone" value={phoneDisplay} />
                <DetailRow label="Email" value={prelead?.email} />
                <DetailRow
                  label="Status"
                  value={prelead?.state ? formatStatusLabel(String(prelead.state)) : null}
                />
                <DetailRow
                  label="SLA"
                  value={SLA_LABEL[getSlaColorKey(prelead?.sla_color)]}
                />
                <DetailRow
                  label="Source"
                  value={source ? formatStatusLabel(String(source)) : null}
                />
                <DetailRow label="List" value={list?.name} />
                <DetailRow label="Created" value={formatDate(prelead?.created_at)} />
                {prelead?.is_disqualified ? (
                  <DetailRow label="Disqualified" value="Yes" />
                ) : null}
                {prelead?.reason ? <DetailRow label="Reason" value={prelead.reason} /> : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <PreleadNoteButton row={rowForNotes} onClick={() => setNotesPrelead(rowForNotes)} />
                {renderResponseButton()}
                {renderQualifyActions()}
              </div>
            </>
          )}
        </div>
      </Modal>

      {notesPrelead ? (
        <PreleadLogModal
          prelead={notesPrelead}
          onClose={() => setNotesPrelead(null)}
          onLogAdded={() => refetch()}
        />
      ) : null}

      {showFacebookForm && prelead ? (
        <FacebookMetaFieldsModal
          preLeadId={Number(prelead.id)}
          prelead={prelead}
          onClose={() => setShowFacebookForm(false)}
        />
      ) : null}

      {showGoogleAdsForm && prelead ? (
        <GoogleAdsFieldsModal
          preLeadId={Number(prelead.id)}
          prelead={prelead}
          onClose={() => setShowGoogleAdsForm(false)}
        />
      ) : null}

      {showWebsiteForm && prelead ? (
        <WebsiteFormFieldsModal
          preLeadId={Number(prelead.id)}
          prelead={prelead}
          onClose={() => setShowWebsiteForm(false)}
        />
      ) : null}

      {showWhatsAppAnswers && prelead ? (
        <WhatsAppFlowAnswersModal
          selectedPrelead={prelead}
          onClose={() => setShowWhatsAppAnswers(false)}
        />
      ) : null}

      <PreleadUpdateStatusModal
        open={Boolean(statusUpdateRow)}
        prelead={statusUpdateRow}
        isSubmitting={
          isUpdatingStatus ||
          createPreleadLeadMutation.isPending ||
          unqualifyPreleadMutation.isPending
        }
        onClose={() => {
          if (
            isUpdatingStatus ||
            createPreleadLeadMutation.isPending ||
            unqualifyPreleadMutation.isPending
          ) {
            return;
          }
          setStatusUpdateRow(null);
        }}
        onConfirm={handleUpdateStatus}
      />
    </>
  );
};

export default InboxPreleadDetailModal;
